/**
 * ScaffoldReviewRenderer — the bespoke review surface for the
 * `scaffold-publications` operation (TV1-2145 §3.4, §4, §6 Risk 1).
 *
 * WHY IT EXISTS (Risk 1, load-bearing scope — not polish): the shared
 * `DiffReviewGrid` is UNVIRTUALIZED and comfortable to ~300 rows, while the
 * nominal scaffold run is ~5,000 rows. Rendering the flat grid by default would
 * make the human review gate unusable exactly when it matters most. So the
 * DEFAULT view here is a SUMMARY BY GAP CLASS (§3.4 "a per-run summary by gap
 * class"), and the raw rows are only materialised when the reviewer drills into
 * ONE class — a bounded slice, never the whole change set.
 *
 * PRE-SELECTION IS DELIBERATE (§3.2, ADR-076 D4): every `proposed` row is
 * approved by default, GAPS INCLUDED. The drafts PLUS the gap report are the
 * deliverable; a gap is never a reason to skip or to withhold approval. The
 * parent's approved-set stays authoritative — this component only calls the
 * `onToggleRow` / `onToggleAll` callbacks it is given and renders from
 * `approvedItemIds`; it keeps no selection state of its own.
 *
 * The gaps arrive in `cells.gaps.new` as a string array drawn from the closed
 * set `NO_TITLE | NO_DESCRIPTION | NO_PRICE | NO_CATEGORY |
 * MISSING_ATTRIBUTE:<attributeId> | NO_IMAGES |
 * DUPLICATE_WITH_IMAGES_CANDIDATE`. This renderer ships ALONGSIDE the compute
 * rather than after it, so the cell is read DEFENSIVELY: an absent or malformed
 * gaps cell renders the row under "sin faltantes" instead of crashing the
 * review gate.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Badge, Button, Checkbox, Group, Stack, Table, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import type { ChangeItemType } from '@bett3r-dev/teselly-domain';
import {
  GAP_SEVERITY,
  gapClassOf,
  gapDetailOf,
  type BlockingGapClass,
  type BlockingGapSeverity
} from '@bett3r-dev/teselly-domain/items-publishing/constants';
import DiffReviewGrid, {
  type DiffChangeItem,
  type DiffColumn
} from '@components/application/SpreadsheetEditor/DiffReviewGrid';
import { operationsTestIds } from '@domains/operations/testids';
import {
  registerOperationReviewRenderer,
  type OperationReviewRendererProps
} from './OperationReview';
import classes from './ScaffoldReviewRenderer.module.css';

/** Rows materialised in a drill-down. Bounds the unvirtualized grid (Risk 1). */
const DRILLDOWN_ROW_CAP = 200;

/** The synthetic bucket for a `proposed` row that carries no blocking gaps. */
const READY_BUCKET = '__ready__';

/**
 * TV1-2145 — severity and grouping come from the SHARED domain taxonomy, never from a
 * copy kept here.
 *
 * An earlier draft of this file declared its own `GAP_SEVERITY` table, which is a second
 * independently-maintained definition of a rule the server also applies: add a gap class
 * server-side and this table silently mis-colours it, or worse, a severity is changed in
 * one place only and the review screen tells the operator something the backend
 * disagrees with. That is the exact divergence ADR-077 forbids ("a second call site is
 * forbidden") and that this ticket's own decision log (DL-05) was written to prevent.
 *
 * Imported through the `/items-publishing/constants` subpath because these are runtime
 * VALUES — the package root would pull the whole domain surface into the bundle. Types
 * still come from the root, as the neighbouring renderers do.
 */
const severityColorMap = { blocking: 'red', warning: 'yellow', hint: 'blue' } as const;

/** Display labels are a FRONTEND concern and correctly live here — the domain owns the
 * vocabulary and its severity, not its Spanish rendering. */
const GAP_LABEL: Record<string, string> = {
  NO_TITLE: 'Sin título',
  NO_DESCRIPTION: 'Sin descripción',
  NO_PRICE: 'Sin precio',
  NO_CATEGORY: 'Sin categoría',
  MISSING_ATTRIBUTE: 'Atributos requeridos faltantes',
  NO_IMAGES: 'Sin imágenes',
  DUPLICATE_WITH_IMAGES_CANDIDATE: 'Posible duplicado con imágenes'
};

const severityColor = ( severity: BlockingGapSeverity ): string => severityColorMap[ severity ];

const severityLabel = ( severity: BlockingGapSeverity ): string => {
  if ( severity === 'blocking' ) return 'bloqueante';
  if ( severity === 'warning' ) return 'advertencia';
  return 'sugerencia';
};

/**
 * Read `cells.gaps.new` DEFENSIVELY — an absent cell, a non-array value, or a
 * non-string entry yields an empty gap list rather than an exception.
 */
const readGaps = ( item: ChangeItemType ): string[] => {
  const cell = ( item.cells as Record<string, { new?: unknown }> | undefined )?.gaps?.new;
  if ( !Array.isArray( cell )) return [];
  return cell.filter(( gap ): gap is string => typeof gap === 'string' );
};

const isSkipped = ( item: ChangeItemType ): boolean =>
  item.status === 'skipped' || item.status === 'rejected';

type GapBucket = {
  gapClass: string;
  severity: BlockingGapSeverity;
  items: ChangeItemType[];
  /** Distinct `<attributeId>` suffixes seen for a parameterised class. */
  details: string[];
};

type ScaffoldSummary = {
  buckets: GapBucket[];
  ready: ChangeItemType[];
  skipped: ChangeItemType[];
  /** Skip reasons grouped by `note` — the structural skips of §3.2. */
  skipReasons: Array<{ note: string; count: number }>;
  proposedCount: number;
};

const summarise = ( changeItems: ChangeItemType[]): ScaffoldSummary => {
  const bucketMap = new Map<string, GapBucket>();
  const ready: ChangeItemType[] = [];
  const skipped: ChangeItemType[] = [];
  const skipReasonMap = new Map<string, number>();
  let proposedCount = 0;

  changeItems.forEach(( item ) => {
    if ( isSkipped( item )) {
      skipped.push( item );
      const note = item.note ?? 'Sin motivo declarado';
      skipReasonMap.set( note, ( skipReasonMap.get( note ) ?? 0 ) + 1 );
      return;
    }

    proposedCount += 1;
    const gaps = readGaps( item );
    if ( gaps.length === 0 ) {
      ready.push( item );
      return;
    }

    // A row lands in EVERY class it is blocked by, so the counts read as "how
    // many drafts need this fixed" — they intentionally do not sum to the row
    // total.
    new Set( gaps.map( gapClassOf )).forEach(( gapClass ) => {
      const bucket = bucketMap.get( gapClass ) ?? {
        gapClass,
        severity: GAP_SEVERITY[ gapClass as BlockingGapClass ] ?? 'blocking',
        items: [],
        details: []
      };
      bucket.items.push( item );
      bucketMap.set( gapClass, bucket );
    });

    gaps.forEach(( gap ) => {
      const detail = gapDetailOf( gap );
      if ( !detail ) return;
      const bucket = bucketMap.get( gapClassOf( gap ));
      if ( bucket && !bucket.details.includes( detail )) bucket.details.push( detail );
    });
  });

  const severityOrder: BlockingGapSeverity[] = [ 'blocking', 'warning', 'hint' ];
  const buckets = Array.from( bucketMap.values()).sort(
    ( a, b ) =>
      severityOrder.indexOf( a.severity ) - severityOrder.indexOf( b.severity ) ||
      b.items.length - a.items.length
  );

  return {
    buckets,
    ready,
    skipped,
    skipReasons: Array.from( skipReasonMap.entries())
      .map(([ note, count ]) => ({ note, count }))
      .sort(( a, b ) => b.count - a.count ),
    proposedCount
  };
};

/**
 * Adapt a domain `ChangeItemType` to the grid's `DiffChangeItem` (the domain's
 * `'proposed'` is the grid's `'pending'` — same semantics, different union), and
 * normalize each cell's optional `old`/`new` into the grid's required pair.
 */
const toDiffItem = ( item: ChangeItemType ): DiffChangeItem => {
  const cells = Object.entries( item.cells ?? {}).reduce<DiffChangeItem['cells']>(
    ( acc, [ key, cell ]) => {
      acc[ key ] = { old: cell?.old ?? null, new: cell?.new ?? null };
      return acc;
    },
    {}
  );
  return {
    targetId: item.targetId,
    targetType: item.targetType,
    cells,
    status: item.status === 'proposed' ? 'pending' : item.status,
    note: item.note
  };
};

const buildColumns = ( items: ChangeItemType[]): DiffColumn[] => {
  const cellKeys = new Set<string>();
  items.forEach(( item ) => {
    Object.keys( item.cells ?? {}).forEach(( key ) => cellKeys.add( key ));
  });
  return [
    { key: 'targetId', label: 'Publicación', affected: false },
    ...Array.from( cellKeys ).map(( key ) => ({ key, label: key, affected: true }))
  ];
};

const ScaffoldReviewRenderer = ({
  changeItems,
  approvedItemIds,
  onToggleRow,
  onToggleAll
}: OperationReviewRendererProps ): ReactElement => {
  const summary = useMemo(() => summarise( changeItems ), [changeItems]);
  const [ drillClass, setDrillClass ] = useState<string | null>( null );
  const approved = useMemo(() => new Set( approvedItemIds ), [approvedItemIds]);

  // Pre-select every `proposed` row, gaps included (§3.2 / ADR-076 D4). Done
  // ONCE, and only while nothing is approved yet, so a reviewer who
  // deliberately clears the selection is not fought by this effect. The parent
  // store remains the authority for the approved set.
  const preselectedRef = useRef( false );
  useEffect(() => {
    if ( preselectedRef.current ) return;
    if ( summary.proposedCount === 0 ) return;
    if ( approvedItemIds.length > 0 ) {
      preselectedRef.current = true;
      return;
    }
    preselectedRef.current = true;
    onToggleAll( true );
  }, [ summary.proposedCount, approvedItemIds.length, onToggleAll ]);

  const allApproved =
    summary.proposedCount > 0 &&
    changeItems
      .filter(( item ) => !isSkipped( item ))
      .every(( item ) => approved.has( item.targetId ));

  const drillItems = useMemo(() => {
    if ( drillClass === null ) return [];
    if ( drillClass === READY_BUCKET ) return summary.ready;
    return summary.buckets.find(( b ) => b.gapClass === drillClass )?.items ?? [];
  }, [ drillClass, summary ]);

  const boundedDrillItems = useMemo(
    () => drillItems.slice( 0, DRILLDOWN_ROW_CAP ),
    [drillItems]
  );
  const diffItems = useMemo(
    () => boundedDrillItems.map( toDiffItem ),
    [boundedDrillItems]
  );
  const columns = useMemo(
    () => buildColumns( boundedDrillItems ),
    [boundedDrillItems]
  );

  if ( drillClass !== null ) {
    const heading =
      drillClass === READY_BUCKET
        ? 'Listos — sin faltantes'
        : GAP_LABEL[ drillClass ] ?? drillClass;
    return (
      <Stack gap="md" data-testid={operationsTestIds.scaffoldDrilldown}>
        <Group justify="space-between">
          <Group gap="xs">
            <Button
              variant="subtle"
              size="compact-sm"
              radius="xs"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => setDrillClass( null )}
              data-testid={operationsTestIds.scaffoldBack}
            >
              Volver al resumen
            </Button>
            <Text fz="sm" fw={600}>{heading}</Text>
            <Badge size="sm" color="gray" variant="light">
              {drillItems.length}
            </Badge>
          </Group>
        </Group>

        {drillItems.length > boundedDrillItems.length && (
          <Text className={classes.draftsNotice}>
            {`Se muestran las primeras ${ boundedDrillItems.length } de ${ drillItems.length } filas de esta clase.`}
          </Text>
        )}

        <DiffReviewGrid
          items={diffItems}
          columns={columns}
          approvedIds={approvedItemIds}
          onToggleRow={onToggleRow}
          onToggleAll={onToggleAll}
        />
      </Stack>
    );
  }

  return (
    <Stack gap="md" data-testid={operationsTestIds.scaffoldSummary}>
      <Group justify="space-between">
        <Checkbox
          label="Aprobar todos"
          checked={allApproved}
          onChange={( e ) => onToggleAll( e.currentTarget.checked )}
          data-testid={operationsTestIds.scaffoldApproveAll}
        />
        <Text fz="sm" c="dimmed">
          {`${ summary.proposedCount } borradores propuestos · ${ approved.size } aprobados`}
        </Text>
      </Group>

      {/* Aprobar no publica nada: la operación es internal-reversible y sólo
          crea borradores (status notPublished, sin channelPublicationId). */}
      <Text className={classes.draftsNotice} data-testid={operationsTestIds.scaffoldDraftsNotice}>
        Aprobar crea borradores en Teselly: no publica ni envía nada al canal.
      </Text>

      <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Faltante</Table.Th>
            <Table.Th>Severidad</Table.Th>
            <Table.Th>Borradores</Table.Th>
            <Table.Th>Detalle</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <Table.Tr
            className={classes.summaryRow}
            onClick={() => setDrillClass( READY_BUCKET )}
            data-testid={operationsTestIds.scaffoldSummaryRow( 'ready' )}
          >
            <Table.Td>
              <Text fz="sm" fw={500}>Listos — sin faltantes</Text>
            </Table.Td>
            <Table.Td>
              <Badge size="sm" color="green" variant="light">ok</Badge>
            </Table.Td>
            <Table.Td>{summary.ready.length}</Table.Td>
            <Table.Td />
          </Table.Tr>

          {summary.buckets.map(( bucket ) => (
            <Table.Tr
              key={bucket.gapClass}
              className={classes.summaryRow}
              onClick={() => setDrillClass( bucket.gapClass )}
              data-testid={operationsTestIds.scaffoldSummaryRow( bucket.gapClass )}
            >
              <Table.Td>
                <Text fz="sm" fw={500}>
                  {GAP_LABEL[ bucket.gapClass ] ?? bucket.gapClass}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge size="sm" color={severityColor( bucket.severity )} variant="light">
                  {severityLabel( bucket.severity )}
                </Badge>
              </Table.Td>
              <Table.Td>{bucket.items.length}</Table.Td>
              <Table.Td>
                {bucket.details.length > 0 && (
                  <Text fz="xs" c="dimmed">{bucket.details.join( ' · ' )}</Text>
                )}
              </Table.Td>
            </Table.Tr>
          ))}

          {summary.skipped.length > 0 && (
            <Table.Tr data-testid={operationsTestIds.scaffoldSummaryRow( 'skipped' )}>
              <Table.Td>
                <Text fz="sm" fw={500}>Omitidos</Text>
                <Stack gap={2} mt={4}>
                  {summary.skipReasons.map(( reason ) => (
                    <Text key={reason.note} className={classes.skipReason}>
                      {`${ reason.note } · ${ reason.count }`}
                    </Text>
                  ))}
                </Stack>
              </Table.Td>
              <Table.Td>
                <Badge size="sm" color="gray" variant="light">omitido</Badge>
              </Table.Td>
              <Table.Td>{summary.skipped.length}</Table.Td>
              <Table.Td />
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      {changeItems.length === 0 && (
        <Text fz="sm" c="dimmed" ta="center" py="md">
          No hay cambios propuestos para esta operación.
        </Text>
      )}
    </Stack>
  );
};

// Register into the per-operationType renderer registry (ADR-070). The
// operations barrel value-exports this module so the registration runs once at
// app load; anything unregistered still falls back to DiffReviewGrid.
registerOperationReviewRenderer( 'scaffold-publications', ScaffoldReviewRenderer );

export default ScaffoldReviewRenderer;
